// @ts-check
// Linha "Comunicações de marketing" na ficha da pessoa e "Marketing do contato principal" no card (2.0.0, contrato 09 de
// campanhas base, seção 6.9): situação da LGPD não consentida, base legal e as ações de marcar e registrar. Lê a
// situação ao abrir a ficha (nunca nas listas) e de novo quando a pessoa ou o card mudam no feed de mudanças. Sem
// permissão (403) ou fora do escopo (404), a linha some sem aviso. Os diálogos abertos pelo menu de comandos também
// moram aqui, porque os comandos da ficha só existem com a página do registro aberta.
import { html } from 'htm/preact';
import { signal } from '@preact/signals';
import { useEffect, useState } from 'preact/hooks';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { remoteRecordChangeState } from '../../data/live-updates.js';
import { fetchMarketingStatus } from '../../data/marketing-consent.js';
import { getRecordLabel } from '../../lib/field-metadata.js';
import { formatInstantDate } from '../../lib/format.js';
import { describeMarketingStatus, marketingRowTarget } from '../../lib/marketing-consent-display.js';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Skeleton } from '../../ui/feedback.js';
import { t } from '../../i18n/index.js';
import { MarkMarketingOptOutDialog, RegisterMarketingConsentDialog } from './marketing-consent-dialogs.js';

/**
 * @typedef {import('../../data/marketing-consent.js').MarketingStatus} MarketingStatus
 * @typedef {{ kind: 'mark' | 'register', personId: string, personLabel: string, isMinor: boolean, onDone?: (status: MarketingStatus) => void }} MarketingConsentDialogRequest
 */

/** Diálogo aberto (pela linha ou pelo menu de comandos). @type {import('@preact/signals').Signal<MarketingConsentDialogRequest | null>} */
export const marketingConsentDialogState = signal(null);
// a linha aparece na página e no painel lateral ao mesmo tempo (painel aberto sobre a página): só a primeira montada
// hospeda o diálogo, para ele não abrir duas vezes
const dialogHostOwnersState = signal(/** @type {number[]} */ ([]));
let dialogHostSequence = 0;

/** A linha que hospeda o diálogo agora (a primeira montada que continua na tela). */
function useIsDialogHost() {
  const [ownerId] = useState(() => (dialogHostSequence += 1));
  useEffect(() => {
    dialogHostOwnersState.value = [...dialogHostOwnersState.value, ownerId];
    return () => {
      dialogHostOwnersState.value = dialogHostOwnersState.value.filter((id) => id !== ownerId);
    };
  }, [ownerId]);
  return dialogHostOwnersState.value[0] === ownerId;
}
// cada marcação ou registro bem-sucedido avança: a linha relê a situação
const marketingStatusVersionState = signal(0);

/** @param {MarketingConsentDialogRequest} request */
export function openMarketingConsentDialog(request) {
  marketingConsentDialogState.value = request;
}

function MarketingConsentDialogHost() {
  const request = marketingConsentDialogState.value;
  if (!request) return null;
  const close = () => {
    marketingConsentDialogState.value = null;
  };
  const done = (/** @type {MarketingStatus} */ status) => {
    close();
    marketingStatusVersionState.value += 1;
    request.onDone?.(status);
  };
  return request.kind === 'mark'
    ? html`<${MarkMarketingOptOutDialog} personId=${request.personId} personLabel=${request.personLabel} onDone=${done} onClose=${close} />`
    : html`<${RegisterMarketingConsentDialog} personId=${request.personId} personLabel=${request.personLabel} isMinor=${request.isMinor} onDone=${done} onClose=${close} />`;
}

/** Nome da pessoa da linha: a ficha aberta ou o contato principal carregado com o card. @param {string} objectNameSingular @param {Record<string, any>} record */
function personLabelOf(objectNameSingular, record) {
  const personObject = objectsByNameSingular.value.get('person');
  const person = objectNameSingular === 'person' ? record : record.pointOfContact;
  return (person && getRecordLabel(personObject, person)) || t('record.untitled');
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: Record<string, any>, onChanged?: () => void }} props
 */
export function MarketingConsentRow({ objectMetadata, record, onChanged }) {
  const target = marketingRowTarget(objectMetadata.nameSingular, record);
  const personId = target?.personId ?? null;
  const [status, setStatus] = useState(/** @type {MarketingStatus | null | 'hidden'} */ (null));
  const version = marketingStatusVersionState.value;
  const remoteChange = remoteRecordChangeState.value;
  const touchesThisRecord = Boolean(personId) && (remoteChange.isReset || remoteChange.keys.has(`person:${personId}`)
    || remoteChange.keys.has(`${objectMetadata.nameSingular}:${record.id}`));
  // muda quando a marca da ficha muda (gravação desta tela ou recarga do registro)
  const markKey = objectMetadata.nameSingular === 'person' ? record.marketingOptOut : record.contactMarketingOptOut;

  useEffect(() => {
    if (!personId) {
      setStatus('hidden');
      return undefined;
    }
    let isCancelled = false;
    fetchMarketingStatus(personId)
      .then((loaded) => !isCancelled && setStatus(loaded))
      .catch((error) => {
        if (isCancelled) return;
        // 403 e 404 somem sem aviso; erro de rede não incomoda a ficha
        if (!(error?.statusCode === 403 || error?.statusCode === 404)) console.error(error);
        setStatus('hidden');
      });
    return () => {
      isCancelled = true;
    };
  }, [personId, version, markKey, touchesThisRecord ? remoteChange.sequence : -1]);

  const isDialogHost = useIsDialogHost();
  const host = isDialogHost ? html`<${MarketingConsentDialogHost} />` : null;
  if (!target || status === 'hidden') return host;
  const label = t(target.variant === 'person' ? 'marketingConsent.row.label' : 'marketingConsent.row.contactLabel');
  if (status === null) {
    return html`<div class="record-fields-card marketing-consent-card">
      <div class="inline-cell marketing-consent-row" data-readonly="true" data-state="loading">
        <div class="inline-cell-label" title=${label}><${Icon} name="IconMail" size=${16} stroke=${1.6} /><span class="inline-cell-label-text">${label}</span></div>
        <div class="marketing-consent-row-value"><${Skeleton} width=${120} height=${16} /></div>
      </div>
    </div>${host}`;
  }

  const display = describeMarketingStatus(status);
  const personLabel = personLabelOf(objectMetadata.nameSingular, record);
  const refresh = (/** @type {MarketingStatus} */ next) => {
    setStatus(next);
    onChanged?.();
  };
  const open = (/** @type {'mark' | 'register'} */ kind) => openMarketingConsentDialog({ kind, personId: target.personId, personLabel, isMinor: status.requiresGuardian === true, onDone: refresh });
  const channelNotes = display.otherChannels.map((channel) => `${t(`operations.privacy.channels.${channel.channel}`)}: ${channel.isEligible
    ? t('operations.privacy.eligibleWithBasis', { basis: t(`operations.privacy.bases.${channel.basis}`) })
    : t(`operations.privacy.blockReasons.${channel.reason ?? 'MARKETING_BLOCKED'}`)}`);

  return html`<div class="record-fields-card marketing-consent-card">
    <div class="inline-cell marketing-consent-row" data-readonly="true" data-state=${display.state} data-testid="marketing-consent-row" data-variant=${target.variant}>
      <div class="inline-cell-label" title=${label}>
        <${Icon} name=${display.state === 'optedOut' ? 'IconForbid' : 'IconMail'} size=${16} stroke=${1.6} />
        <span class="inline-cell-label-text">${label}</span>
      </div>
      <div class="marketing-consent-row-value">
        ${display.state === 'optedOut'
          ? html`<div class="marketing-consent-row-line">
              <span class="marketing-consent-chip"><${Icon} name="IconForbid" size=${12} stroke=${2} />${t('marketingConsent.row.optedOutChip')}</span>
              ${display.optOutAt || display.optOutSource
                ? html`<span class="marketing-consent-row-text">${t('marketingConsent.row.optedOut', {
                    date: display.optOutAt ? formatInstantDate(display.optOutAt) : '-',
                    source: display.optOutSource ? t(`marketingConsent.optOutSources.${display.optOutSource}`) : '-',
                  })}</span>`
                : null}
            </div>
            <span class="marketing-consent-row-hint">${t('marketingConsent.row.serviceContinues')}</span>
            ${display.pendingRequestAt
              ? html`<span class="marketing-consent-row-pending">${t('marketingConsent.row.pendingRequest', { date: formatInstantDate(display.pendingRequestAt) })}</span>`
              : null}`
          : display.state === 'eligible'
            ? html`<span class="marketing-consent-row-text" data-tone="neutral">${t('marketingConsent.row.eligible', { basis: t(`marketingConsent.bases.${display.basis}`) })}</span>`
            : html`<span class="marketing-consent-row-text" data-tone="warning">${t('marketingConsent.row.blocked', {
                reason: t(`operations.privacy.blockReasons.${display.reason ?? 'MARKETING_BLOCKED'}`),
              })}</span>`}
        ${channelNotes.length > 0 ? html`<span class="marketing-consent-row-hint">${channelNotes.join(' · ')}</span>` : null}
        ${display.canMark || display.canRegister
          ? html`<div class="marketing-consent-row-actions">
              ${display.canMark
                ? html`<${Button} title=${t('marketingConsent.row.markAction')} icon="IconForbid" variant="tertiary" accent="danger" size="small" onClick=${() => open('mark')} />`
                : null}
              ${display.canRegister
                ? html`<${Button} title=${t('marketingConsent.row.registerAction')} icon="IconShieldCheck" variant="secondary" size="small" onClick=${() => open('register')} />`
                : null}
            </div>`
          : null}
      </div>
    </div>
  </div>${host}`;
}
